import { ReactNode } from 'react';
import {
  Control,
  FieldError,
  FieldValues,
  Path,
  useFormState,
} from 'react-hook-form';
import { MenuPlacement } from 'react-select';
import styled from 'styled-components';

import { MediaResource } from '@/types/resources';
import { TestingCurrencies } from '@/utils/enums';

import { Label, Larger } from '.';
import Datepicker from '../Datepicker';
import Text from '../Text';
import { Option } from './Dropdown';
import CurrencyField from './Fields/CurrencyField';
import DecimalField from './Fields/DecimalField';
import DurationField from './Fields/DurationField';
import ImageField from './Fields/ImageField';
import IntegerField from './Fields/IntegerField';
import MultiselectField from './Fields/MultiselectField';
import RadioField, { RadioOption } from './Fields/RadioField';
import SelectField from './Fields/SelectField';
import SingleFileField from './Fields/SingleFileField';
import SwitchField from './Fields/SwitchField';
import TextField from './Fields/TextField';
import TextareaField from './Fields/TextareaField';
import VideoFieldV2 from './Fields/VideoFieldV2';
import ValidationMessage from './ValidationMessage';

type FieldType =
  | 'text'
  | 'textarea'
  | 'select'
  | 'multiselect'
  | 'duration'
  | 'datepicker'
  | 'image'
  | 'file'
  | 'switch'
  | 'video'
  | 'currency'
  | 'radio'
  | 'integer'
  | 'decimal';

export interface DefaultProps<T extends FieldValues> {
  id?: string;
  control: Control<T>;
  name: Path<T>;
  placeholder?: string;
  label?: string;
  optional?: boolean;
  labelCounter?: number;
  subLabel?: string;
  type?: FieldType;
  disabled?: boolean;
  className?: string;
  onChangeCallback?: () => void;
  maximumCharacters?: number;
}

export type TextFieldProps = {
  type?: 'text';
  inputType?: 'text' | 'url';
  menuPlacement?: never;
  options?: never;
  datepickerType?: never;
  existingMedia?: never;
  accept?: never;
  notes?: never;
  suggestionsEndpoint?: string;
  description?: never;
  headline?: never;
  currency?: never;
  icon?: never;
  fileUploadedCallback?: never;
  onRemoveExisting?: never;
  maximumCharacters?: number;
  minutesOnly?: never;
  inline?: never;
  disableDayDimming?: never;
};

export type CurrencyFieldProps = {
  type?: 'currency';
  inputType?: never;
  menuPlacement?: never;
  options?: never;
  datepickerType?: never;
  existingMedia?: never;
  accept?: never;
  notes?: never;
  suggestionsEndpoint?: never;
  description?: never;
  headline?: never;
  currency?: TestingCurrencies;
  icon?: never;
  fileUploadedCallback?: never;
  onRemoveExisting?: never;
  minutesOnly?: never;
  inline?: never;
  disableDayDimming?: never;
};

export type TextAreaFieldProps = {
  type: 'textarea';
  inputType?: never;
  menuPlacement?: never;
  options?: never;
  datepickerType?: never;
  existingMedia?: never;
  accept?: never;
  notes?: never;
  suggestionsEndpoint?: never;
  description?: never;
  headline?: never;
  currency?: never;
  icon?: never;
  fileUploadedCallback?: never;
  onRemoveExisting?: never;
  minutesOnly?: never;
  inline?: never;
  disableDayDimming?: never;
};

export type ImageFieldProps = {
  type: 'image';
  inputType?: never;
  menuPlacement?: never;
  options?: never;
  datepickerType?: never;
  id: string;
  existingMedia?: MediaResource;
  accept?: string;
  notes?: ReactNode;
  suggestionsEndpoint?: never;
  description?: never;
  headline?: never;
  currency?: never;
  icon?: never;
  fileUploadedCallback?: never;
  onRemoveExisting?: never;
  minutesOnly?: never;
  inline?: never;
  disableDayDimming?: never;
};

export type DurationFieldProps = {
  type: 'duration';
  minutesOnly?: boolean;
  inputType?: never;
  menuPlacement?: never;
  options?: never;
  datepickerType?: never;
  existingMedia?: never;
  accept?: never;
  notes?: never;
  suggestionsEndpoint?: never;
  description?: never;
  headline?: never;
  currency?: never;
  icon?: never;
  fileUploadedCallback?: never;
  onRemoveExisting?: never;
  inline?: never;
  disableDayDimming?: never;
};

export type IntegerFieldProps = {
  type: 'integer';
  minutesOnly?: never;
  inputType?: never;
  menuPlacement?: never;
  options?: never;
  datepickerType?: never;
  existingMedia?: never;
  accept?: never;
  notes?: never;
  suggestionsEndpoint?: never;
  description?: never;
  headline?: never;
  currency?: never;
  icon?: never;
  fileUploadedCallback?: never;
  onRemoveExisting?: never;
  inline?: never;
  disableDayDimming?: never;
};

// Like `integer`, but keeps up to two decimal places — for values the backend
// stores as fractions (e.g. accrued vacation days) where rounding on save would
// silently discard them.
export type DecimalFieldProps = {
  type: 'decimal';
  minutesOnly?: never;
  inputType?: never;
  menuPlacement?: never;
  options?: never;
  datepickerType?: never;
  existingMedia?: never;
  accept?: never;
  notes?: never;
  suggestionsEndpoint?: never;
  description?: never;
  headline?: never;
  currency?: never;
  icon?: never;
  fileUploadedCallback?: never;
  onRemoveExisting?: never;
  inline?: never;
  disableDayDimming?: never;
};

export type DatepickerProps = {
  type: 'datepicker';
  inputType?: never;
  menuPlacement?: never;
  options?: never;
  datepickerType?: 'date' | 'datetime' | 'time';
  existingMedia?: never;
  accept?: never;
  notes?: never;
  suggestionsEndpoint?: never;
  description?: never;
  headline?: never;
  disabled?: boolean;
  currency?: never;
  icon?: never;
  fileUploadedCallback?: never;
  onRemoveExisting?: never;
  minutesOnly?: never;
  inline?: never;
  disableDayDimming?: boolean;
};

export type SelectFieldProps<OptionT> = {
  type: 'select';
  inputType?: never;
  menuPlacement?: MenuPlacement;
  datepickerType?: never;
  options: Option<OptionT>[];
  existingMedia?: never;
  accept?: never;
  notes?: never;
  suggestionsEndpoint?: never;
  description?: never;
  headline?: never;
  currency?: never;
  icon?: never;
  fileUploadedCallback?: never;
  onRemoveExisting?: never;
  minutesOnly?: never;
  inline?: never;
  disableDayDimming?: never;
};

export type MultiSelectFieldProps<OptionT> = {
  type: 'multiselect';
  inputType?: never;
  menuPlacement?: MenuPlacement;
  datepickerType?: never;
  options: Option<OptionT>[];
  existingMedia?: never;
  accept?: never;
  notes?: never;
  suggestionsEndpoint?: never;
  description?: never;
  headline?: never;
  currency?: never;
  icon?: never;
  fileUploadedCallback?: never;
  onRemoveExisting?: never;
  minutesOnly?: never;
  inline?: never;
  disableDayDimming?: never;
};

export type FileFieldProps = {
  type: 'file';
  inputType?: never;
  menuPlacement?: never;
  options?: never;
  datepickerType?: never;
  id: string;
  accept?: string;
  notes?: ReactNode;
  existingMedia?: MediaResource;
  suggestionsEndpoint?: never;
  description?: never;
  headline?: never;
  currency?: never;
  icon?: 'image' | 'video' | 'file';
  fileUploadedCallback?: never;
  onRemoveExisting?: () => void;
  minutesOnly?: never;
  inline?: never;
  disableDayDimming?: never;
};

export type VideoFieldProps = {
  type: 'video';
  inputType?: never;
  menuPlacement?: never;
  options?: never;
  datepickerType?: never;
  id: string;
  accept?: string;
  notes?: ReactNode;
  existingMedia?: MediaResource;
  suggestionsEndpoint?: never;
  description?: never;
  headline?: never;
  currency?: never;
  icon?: never;
  fileUploadedCallback?: (file: File) => void;
  onRemoveExisting?: never;
  minutesOnly?: never;
  inline?: never;
  disableDayDimming?: never;
};

export type SwitchFieldProps = {
  type: 'switch';
  inputType?: never;
  menuPlacement?: never;
  options?: never;
  datepickerType?: never;
  id?: string;
  accept?: never;
  notes?: never;
  existingMedia?: never;
  suggestionsEndpoint?: never;
  description?: string;
  headline: string;
  currency?: never;
  icon?: never;
  fileUploadedCallback?: never;
  onRemoveExisting?: never;
  minutesOnly?: never;
  inline?: never;
  disableDayDimming?: never;
};

export type RadioFieldProps<OptionT> = {
  type: 'radio';
  inline?: boolean;
  inputType?: never;
  menuPlacement?: never;
  datepickerType?: never;
  options: RadioOption<OptionT>[];
  existingMedia?: never;
  accept?: never;
  notes?: never;
  suggestionsEndpoint?: never;
  description?: never;
  headline?: never;
  currency?: never;
  icon?: never;
  fileUploadedCallback?: never;
  onRemoveExisting?: never;
  minutesOnly?: never;
  disableDayDimming?: never;
};

type ConditionalProps<OptionT> =
  | TextFieldProps
  | TextAreaFieldProps
  | DurationFieldProps
  | DatepickerProps
  | MultiSelectFieldProps<OptionT>
  | ImageFieldProps
  | FileFieldProps
  | VideoFieldProps
  | SelectFieldProps<OptionT>
  | SwitchFieldProps
  | CurrencyFieldProps
  | RadioFieldProps<OptionT>
  | IntegerFieldProps
  | DecimalFieldProps;

const FieldNumber = styled(Text).attrs({
  $type: 'extraSmall',
  $color: 'white',
})`
  background-color: #5d54d6;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  margin-left: 8px;
  transform: translateY(-2px);
`;

type Props<T extends FieldValues, OptionT> = DefaultProps<T> &
  ConditionalProps<OptionT>;

const FormField = <T extends FieldValues, OptionT = string>({
  name,
  control,
  id,
  label,
  optional,
  subLabel,
  labelCounter,
  placeholder,
  disabled = false,
  inputType = 'text',
  fileUploadedCallback,
  onRemoveExisting,
  onChangeCallback,
  type = 'text',
  icon = 'file',
  className,
  menuPlacement,
  options,
  datepickerType,
  existingMedia,
  accept,
  notes,
  suggestionsEndpoint,
  description,
  currency,
  headline,
  maximumCharacters,
  minutesOnly,
  inline,
  disableDayDimming,
}: Props<T, OptionT>) => {
  const { errors } = useFormState<T>({
    control,
    name,
    exact: true,
  });

  const renderInput = () => {
    switch (type) {
      case 'textarea':
        return (
          <TextareaField<T>
            disabled={disabled}
            placeholder={placeholder ?? `Add ${name}`}
            name={name}
            control={control}
            id={id}
          />
        );
      case 'currency':
        return (
          <CurrencyField<T>
            currency={currency}
            disabled={disabled}
            placeholder={placeholder ?? `Add ${name}`}
            name={name}
            control={control}
            id={id}
          />
        );

      case 'radio':
        return (
          <RadioField<T, OptionT>
            name={name}
            control={control}
            options={options ?? []}
            inline={inline}
          />
        );
      case 'integer':
        return (
          <IntegerField<T> name={name} control={control} disabled={disabled} />
        );
      case 'decimal':
        return (
          <DecimalField<T> name={name} control={control} disabled={disabled} />
        );
      case 'select':
        return (
          <SelectField<T, OptionT>
            name={name}
            control={control}
            menuPlacement={menuPlacement}
            disabled={disabled}
            options={options ?? []}
            id={id}
            placeholder={placeholder}
            onChangeCallback={onChangeCallback}
          />
        );
      case 'multiselect':
        return (
          <MultiselectField<T, OptionT>
            menuPlacement={menuPlacement}
            disabled={disabled}
            control={control}
            name={name}
            options={options!}
            id={id}
            placeholder={placeholder}
          />
        );
      case 'duration':
        return (
          <DurationField<T>
            name={name}
            control={control}
            minutesOnly={minutesOnly}
          />
        );
      case 'datepicker':
        return (
          <Datepicker<T>
            disabled={disabled}
            control={control}
            name={name}
            type={datepickerType}
            disableDayDimming={disableDayDimming}
          />
        );
      case 'image':
        return (
          <ImageField<T>
            control={control}
            name={name}
            id={id!}
            existingMedia={existingMedia}
            notes={notes}
            accept={accept}
            disabled={disabled}
          />
        );
      case 'file':
        return (
          <SingleFileField<T>
            control={control}
            name={name}
            id={id!}
            icon={icon}
            existingMedia={existingMedia}
            onRemoveExisting={onRemoveExisting}
            accept={accept}
            notes={notes}
          />
        );
      case 'video':
        return (
          <VideoFieldV2<T>
            control={control}
            name={name}
            id={id!}
            existingMedia={existingMedia}
            accept={accept}
            notes={notes}
            fileUploadedCallback={fileUploadedCallback}
          />
        );
      case 'switch':
        return (
          <SwitchField<T>
            control={control}
            name={name}
            id={id}
            headline={headline!}
            description={description}
            disabled={disabled}
          />
        );
      default:
        return (
          <>
            <TextField<T>
              control={control}
              name={name}
              id={id}
              disabled={disabled}
              inputType={inputType}
              placeholder={placeholder ?? `Add ${name}`}
              suggestionsEndpoint={suggestionsEndpoint}
              maximumCharacters={maximumCharacters}
            />
          </>
        );
    }
  };

  const path = name.split('.');

  const error = path.reduce((acc: any, curr: string) => {
    return acc?.[curr];
  }, errors);

  return (
    <Larger className={className}>
      {!!label && (
        <Label htmlFor={id}>
          {label}
          {!!subLabel && (
            <Text $color="gray" $type="smallBold" as="span">
              {' '}
              {subLabel}
            </Text>
          )}
          {!!optional && (
            <Text $color="gray" $type="smallBold" as="span">
              {' '}
              (Optional)
            </Text>
          )}
          {!!labelCounter && <FieldNumber>{labelCounter}</FieldNumber>}
        </Label>
      )}
      {renderInput()}
      {!!error && (
        <ValidationMessage>{(error as FieldError).message}</ValidationMessage>
      )}
    </Larger>
  );
};

export default FormField;
